import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { fetchWidgetConfig } from "@/widget/api";
import { ShadowMount } from "@/widget/WidgetShadowMount";
import { WidgetShell } from "@/widget/WidgetShell";
import { WidgetErrorBoundary } from "@/widget/WidgetErrorBoundary";
import { WidgetMessage } from "@/widget/components/shared";
import type { WidgetConfigData } from "@/widget/types";

/**
 * Standalone widget route — renders the real frontend widget (WidgetShell in a
 * shadow root, exactly as the production embed mounts it) for a single widget id.
 *
 * Two consumers:
 *  - the business portal Add/Edit **live preview**: iframes this route and drives
 *    it via postMessage (see protocol below) so the preview updates as the form
 *    is edited — no server round-trip, the actual frontend widget;
 *  - a crawlable standalone fallback (plan D5): a saved widget renders itself.
 *
 * postMessage protocol (every message tagged `source: "sociability-widget-preview"`):
 *   parent → iframe:
 *     { type: "overrides", overrides, status? }  presentation fields shallow-merged
 *                                                onto the fetched saved config (Edit)
 *     { type: "config",    config, status? }     a full resolved config (Add / unsaved,
 *                                                where there is no saved widget to fetch)
 *   iframe → parent:
 *     { type: "ready" }              posted once the route is listening
 *     { type: "size", height }       content height (ResizeObserver) for iframe auto-sizing
 *
 * All browser APIs are touched only inside effects, so the route is SSR-safe:
 * the server renders an empty host and the widget mounts on hydration.
 */
export const Route = createFileRoute("/widget/$widgetId")({
  component: WidgetStandalonePage,
});

const SOURCE = "sociability-widget-preview";

type PreviewStatus = "active" | "inactive";

function isPreviewStatus(v: unknown): v is PreviewStatus {
  return v === "active" || v === "inactive";
}

function WidgetStandalonePage() {
  const { widgetId } = Route.useParams();

  const [base, setBase] = useState<WidgetConfigData | null>(null);
  const [overrides, setOverrides] = useState<Partial<WidgetConfigData>>({});
  const [status, setStatus] = useState<PreviewStatus>("active");
  const [loading, setLoading] = useState(true);

  const rootRef = useRef<HTMLDivElement>(null);
  // Origin of the embedding parent, captured from its first message so size
  // updates post back only to it (not "*") once known.
  const parentOriginRef = useRef<string>("*");

  // Self-fetch the saved widget's resolved config. A parent `config` message can
  // replace it (Add/unsaved) and `overrides` layer live edits on top (Edit).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchWidgetConfig(widgetId)
      .then((res) => {
        if (cancelled) return;
        if (res.data && isPreviewStatus(res.status)) {
          setBase(res.data);
          setStatus(res.status);
        }
      })
      .catch(() => {
        /* no saved config (e.g. Add) — the parent may drive via postMessage */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [widgetId]);

  // Listen for parent-driven config / live overrides; announce readiness.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as
        | { source?: string; type?: string; config?: WidgetConfigData; overrides?: Partial<WidgetConfigData>; status?: unknown }
        | null;
      if (!data || data.source !== SOURCE) return;

      parentOriginRef.current = e.origin || "*";

      if (data.type === "config" && data.config) {
        // `config` sets the base (venue + curation). Live presentation `overrides`
        // are a separate channel that always reflects the current form, so they are
        // NOT cleared here — they layer on top of whichever base is current.
        setBase(data.config);
        setLoading(false);
        if (isPreviewStatus(data.status)) setStatus(data.status);
      } else if (data.type === "overrides") {
        setOverrides(data.overrides ?? {});
        if (isPreviewStatus(data.status)) setStatus(data.status);
      }
    };

    window.addEventListener("message", onMessage);
    // window.parent === window when not embedded — harmless (no listener).
    window.parent?.postMessage({ source: SOURCE, type: "ready" }, "*");
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Report content height to the parent so it can size the iframe to the widget.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const postSize = () => {
      window.parent?.postMessage(
        { source: SOURCE, type: "size", height: el.scrollHeight },
        parentOriginRef.current,
      );
    };
    const ro = new ResizeObserver(postSize);
    ro.observe(el);
    postSize();
    return () => ro.disconnect();
  }, []);

  const effective = base ? ({ ...base, ...overrides } as WidgetConfigData) : null;

  return (
    // This route only ever serves the portal's iframe preview and the crawlable
    // standalone fallback — never the production embed (entry.tsx mounts WidgetShell
    // directly into the customer's own container). So it's safe to pad/centre the
    // widget here without touching WidgetShell/ShadowMount, which must stay
    // pixel-identical to the real embed. Without this, "Embedded Inline" (the only
    // format that's always visible, with its own bordered card) renders flush
    // against the iframe/viewport edge.
    <div style={{ display: "flex", justifyContent: "center", padding: 16, boxSizing: "border-box" }}>
      <div ref={rootRef} style={{ maxWidth: "100%" }}>
        <ShadowMount>
          {effective ? (
            <WidgetErrorBoundary>
              <WidgetShell config={effective} status={status} />
            </WidgetErrorBoundary>
          ) : loading ? (
            <WidgetMessage>Loading…</WidgetMessage>
          ) : null}
        </ShadowMount>
      </div>
    </div>
  );
}
